import React, { useState, useRef, FC, useEffect, useCallback } from 'react';
import { ListBox } from 'primereact/listbox';
import { useTranslation } from 'localization';
import { Sphere } from 'types/global';
import { fetchPotentialAssigneesData } from '@center-platform/spheres/identity';
import type { GlyphColor } from '../../../../types/glyph';
import GlyphAtom from '../../../atoms/Glyph/GlyphAtom';
import './PerspectiveSearch.scss';
import { Skeleton } from 'primereact/skeleton';
import { Chip } from 'primereact/chip';
import { Badge } from 'primereact/badge';
import { useApiClientsOrNull } from '../../../../context/ApiClientsContext';

export interface OptionType {
  name: string;
  code: string;
  glyph?: string;
  glyphColor?: GlyphColor;
  imageId?: string;
}

export interface PerspectiveFilter {
  perspectiveSystemName: string;
  operator: 'in' | 'not in';
  assignees: string[];
}

interface PerspectiveSearchProps {
  perspectiveSystemName: string;
  schemaSystemName: string;
  sphereId: string;
  direction?: 'outwards' | 'inwards' | 'both'; // Direction for search traversal
  hops?: number; // Number of hops for search traversal
  onAssigneeSelected?: (
    filter: PerspectiveFilter,
    selectedOptions: OptionType[]
  ) => void;
  showLabel?: boolean;
  label?: React.ReactNode;
  isEditing?: boolean;
  stayOpen?: boolean;
  className?: string;
  isAtlas?: boolean;
  initialSelectedOptions?: OptionType[];
}

const PerspectiveSearch: FC<PerspectiveSearchProps> = ({
  perspectiveSystemName,
  schemaSystemName,
  sphereId,
  direction = 'outwards', // Default to outwards if not provided
  hops = 1, // Default to 1 hop if not provided
  onAssigneeSelected,
  showLabel = false,
  label = '',
  isEditing = false,
  stayOpen = false,
  className = '',
  isAtlas = false,
  initialSelectedOptions = [],
}) => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();

  if (!apiClients) {
    return null;
  }
  
  const { graphClient } = apiClients;
  const nodeRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Data states
  const [options, setOptions] = useState<OptionType[]>([]);
  const [page, setPage] = useState<number>(1);
  const [, setHasMoreOptions] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedOptions, setSelectedOptions] = useState<OptionType[]>(
    initialSelectedOptions || []
  );
  const MAX_VISIBLE_CHIPS = 5; // Limit visible chips

  const [showAllChips, setShowAllChips] = useState(false);
  const fetchPotentialAssigneesParams = {
    perspectiveSystemName,
    schemaSystemName,
    page,
    limit: 100,
    sphereId,
    direction,
    hops,
    searchType: 'filter-assignees',
  };
  // Depend on initialSelectedOptionsKey (value-equal projection of
  // initialSelectedOptions) rather than initialSelectedOptions itself: the
  // prop's default value (`= []`) and several callers' fallbacks (`|| []`)
  // allocate a new array reference every render, which would refire this
  // effect every render and infinite-loop with setSelectedOptions.
  const initialSelectedOptionsKey = (initialSelectedOptions ?? [])
    .map((option) => option.code)
    .join(',');
  useEffect(() => {
    if (initialSelectedOptions) {
      setSelectedOptions(initialSelectedOptions);
    }
  }, [initialSelectedOptionsKey]);

  const fetchData = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    try {
      
      const potentialAssigneesData = await fetchPotentialAssigneesData(
        graphClient,
        fetchPotentialAssigneesParams
      );

      setHasMoreOptions(potentialAssigneesData.hasNextPage);

      const newOptions = potentialAssigneesData.spheres.map((s: Sphere) => ({
        name: s.properties.name,
        code: s.id,
        glyph: s.glyph,
        glyphColor: s.glyphColor,
        imageId: s.properties.imageId,
      }));

      const mergedOptions = [
        ...newOptions,
        ...selectedOptions.filter(
          (selected) => !newOptions.some((o) => o.code === selected.code)
        ),
      ];

      setOptions(mergedOptions);
    } catch (error: any) {
      setError(t('context.editor.perspectiveFilters.searchFailed'));
    } finally {
      setIsLoading(false);
    }
  }, [fetchPotentialAssigneesParams, selectedOptions]);

  useEffect(() => {
    setPage(1);
    setHasMoreOptions(true);
    setError(null);
    fetchData();
  }, [direction]); // Refetch when direction changes

  // Custom template for ListBox items (adds glyph)
  const itemTemplate = (option: OptionType) => {
    return (
      <div className="flex align-items-center">
        <GlyphAtom
          glyph={option.glyph}
          glyphColor={option.glyphColor}
          fallbackText={option.name}
          className="avatar-image"
        />
        <span>{option.name}</span>
      </div>
    );
  };

  // Handle Listbox selection changes
  const handleChange = (e: { value: OptionType[] }) => {
    setSelectedOptions(e.value);
    const filter: PerspectiveFilter = {
      perspectiveSystemName,
      operator: 'in',
      assignees: e.value.map((option) => option.code),
    };

    onAssigneeSelected?.(filter, e.value);
  };

  // Remove an item from the selected list (chip removal)
  const removeSelection = (code: string) => {
    const newSelectedOptions = selectedOptions.filter(
      (option) => option.code !== code
    );
    setSelectedOptions(newSelectedOptions);
    const filter: PerspectiveFilter = {
      perspectiveSystemName,
      operator: 'in',
      assignees: newSelectedOptions.map((option) => option.code),
    };
    onAssigneeSelected?.(filter, newSelectedOptions);
  };

  const clearSelections = () => {
    const clearedOptions: OptionType[] = [];

    setSelectedOptions(clearedOptions);

    // Immediately notify parent with truly cleared state
    const filter: PerspectiveFilter = {
      perspectiveSystemName,
      operator: 'in',
      assignees: [],
    };

    // Send empty array, not stale state
    onAssigneeSelected?.(filter, clearedOptions);
  };

  const toggleShowAll = () => {
    setShowAllChips(!showAllChips);
  };
  useEffect(() => {
    setShowAllChips(false); // Close expanded chips when new selection is made
  }, [selectedOptions]);

  // PrimeReact's ListBox always renders two `aria-hidden` focus-trap sentinel
  // spans (`p-hidden-accessible p-hidden-focusable`) with `tabIndex={0}`, meant
  // to wrap Tab focus back into the box when it is the sole focus scope. That
  // conflicts with axe's `aria-hidden-focus` rule the instant this ListBox
  // renders inline (not inside a dialog/overlay that manages its own focus
  // scope) — as it now always does, both here and in a `focusPerspective`
  // column's draft editor (`LevelFilterPopoverOrganism`'s narrow-layout tree
  // mount). Neutralize them the same way `CollapsibleSectionAtom` takes
  // collapsed content out of the tab order: not focusable, since nothing here
  // relies on the wrap-around trap.
  useEffect(() => {
    if (isLoading) return;
    const sentinels = dropdownRef.current?.querySelectorAll<HTMLElement>(
      '[data-pc-section="hiddenfirstfocusableel"], [data-pc-section="hiddenlastfocusableel"]'
    );
    sentinels?.forEach((el) => el.setAttribute('tabindex', '-1'));
  }, [isLoading, options]);

  return (
    <div
      ref={nodeRef}
      className={`node-perspective-search align-items-center d-flex w-full ${
        isEditing ? 'editing' : ''
      }`}
    >
      <div className="perspective-search-container" ref={dropdownRef}>
        {isLoading ? (
          <div data-testid="perspective-search-loading">
            {Array.from({ length: 6 }).map(() => (
              <Skeleton
                key={crypto.randomUUID()}
                shape="rectangle"
                width="100%"
                className="custom-skeleton mb-2"
              />
            ))}
          </div>
        ) : (
          <ListBox
            value={selectedOptions}
            options={options}
            onChange={handleChange}
            multiple
            optionLabel="name"
            filter
            className="p-listbox p-component"
            itemTemplate={itemTemplate}
            aria-label={t('context.editor.perspectiveFilters.narrowAssignees')}
            filterInputProps={{
              'aria-label': t('context.editor.perspectiveFilters.searchAssignees'),
            }}
            // A listbox may only contain options; PrimeReact's own "no results" row is
            // a plain <li> with no role, so it fails aria-required-children/listitem
            // the moment options is empty (e.g. after a failed fetch). Same remediation
            // as MultiSelectAtom: make it a disabled option.
            pt={{
              emptyMessage: { role: 'option', 'aria-disabled': true, 'aria-selected': false },
            }}
          />
        )}

        {/* Render selected items as chips */}
        <div className="selected-chips-container">
          {/* Show badge if more than MAX_VISIBLE_CHIPS items & not already expanded */}
          {selectedOptions.length > MAX_VISIBLE_CHIPS && !showAllChips && (
            <Badge
              value={`+${selectedOptions.length - MAX_VISIBLE_CHIPS}`}
              severity="info"
              onClick={(e) => {
                e.stopPropagation();
                toggleShowAll();
              }}
              className="extra-items-badge"
              style={{ cursor: 'pointer', marginLeft: '5px' }}
            />
          )}
          {selectedOptions
            .slice(0, showAllChips ? selectedOptions.length : MAX_VISIBLE_CHIPS)
            .map((option) => (
              <Chip
                key={option.code}
                label={option.name}
                removable
                onRemove={(event) => {
                  event.originalEvent?.stopPropagation();
                  removeSelection(option.code);
                  return false;
                }}
                className="selected-chip"
              />
            ))}
        </div>

        <button
          type="button"
          className="p-button clear-button p-button-sm"
          onClick={clearSelections}
          style={{
            marginTop: '0.5rem',
            marginRight: '1rem',
            marginBottom: '0.5rem',
          }}
        >
          {t('context.editor.perspectiveFilters.clear')}
        </button>

        {error && (
          <div className="error-message" data-testid="perspective-search-error">
            <span>{error}</span>
            <button type="button" className="p-button p-button-text p-button-sm" onClick={fetchData}>
              {t('context.editor.perspectiveFilters.retry')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PerspectiveSearch;
